/**
 * InfoSection Component — Below-Fold Content Area
 * =================================================
 * Creates the full scrollable content area below the keyboard dashboard.
 * Includes: How It Works, Diagnostics Guide, Pro Tips, FAQ, and Footer.
 * Ad slots are interleaved between content sections for revenue.
 *
 * SEO Strategy: Rich, helpful content with target keywords naturally embedded.
 * Google rewards pages with comprehensive, user-helpful content.
 */
import { createAdSlot } from '../AdSlot/index.js';
import { t } from '../../services/i18n.js';

export const createInfoSection = () => {
  const container = document.createElement('div');
  container.className = 'cm-below-fold';

  container.innerHTML = `
    <!-- ROW 1: How It Works & Diagnostics -->
    <div class="cm-below-fold-layout">
      <aside class="cm-below-fold-sidebar cm-sidebar-left" id="sidebar-l1-mount"></aside>
      <div class="cm-below-fold-main">
        <!-- ==================== HOW IT WORKS ==================== -->
        <section class="cm-info-section" id="how-it-works">
      <div class="cm-info-header">
        <h2 class="cm-info-title">${t('HDR_HOW_IT_WORKS')}</h2>
        <p class="cm-info-subtitle">${t('HDR_HOW_IT_WORKS_SUB')}</p>
      </div>
      <div class="cm-steps-grid">
        <div class="cm-step-card">
          <div class="cm-step-badge">1</div>
          <h3>${t('INFO_STEP1_TITLE', 'Open & Press')}</h3>
          <p>${t('INFO_STEP1_DESC', 'Open this page in any modern browser. No downloads, no installs. Just start pressing keys on your keyboard — every keypress is detected instantly.')}</p>
        </div>
        <div class="cm-step-card">
          <div class="cm-step-badge">2</div>
          <h3>${t('INFO_STEP2_TITLE', 'Watch & Analyze')}</h3>
          <p>${t('INFO_STEP2_DESC', 'See real-time metrics: <strong>polling rate</strong>, <strong>input latency</strong>, <strong>NKRO rollover</strong>, and <strong>chatter detection</strong>. Press multiple keys simultaneously to test rollover limits.')}</p>
        </div>
        <div class="cm-step-card">
          <div class="cm-step-badge">3</div>
          <h3>${t('INFO_STEP3_TITLE', 'Export & Share')}</h3>
          <p>${t('INFO_STEP3_DESC', 'Generate a <strong>print report</strong> or <strong>JSON export</strong> of your results. Compare keyboards, diagnose issues, or share results with your community.')}</p>
        </div>
      </div>
    </section>

    <!-- ==================== WORLD-CLASS TOOLKIT (FEATURES) ==================== -->
    <section class="cm-info-section" id="all-features">
      <div class="cm-info-header">
        <h2 class="cm-info-title">${t('HDR_FEATURES', 'WORLD-CLASS TOOLKIT')}</h2>
        <p class="cm-info-subtitle">${t('HDR_FEATURES_SUB', 'Everything you need to analyze, diagnose, and optimize your keyboard.')}</p>
      </div>
      <div class="cm-info-grid cm-features-grid">
        <article class="cm-info-card">
          <h3>${t('FEAT_HEATMAP_TITLE', '🔥 Live Heatmap Overlay')}</h3>
          <p>${t('FEAT_HEATMAP_DESC', 'Track your exact keystroke patterns and finger frequency over time. Perfect for discovering ergonomic hotspots or planning custom alternative layouts (Dvorak/Colemak).')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('FEAT_AUDIO_TITLE', '🎵 Dynamic Stereo Audio')}</h3>
          <p>${t('FEAT_AUDIO_DESC', 'Experience procedurally generated, satisfying switch sounds spanning 15+ distinct profiles (Mechanical, Membrane, Silent Red, Electro-Capacitive, etc.) panning in real-time stereo.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('FEAT_LAYOUTS_TITLE', '🗺️ Layout Wizard')}</h3>
          <p>${t('FEAT_LAYOUTS_DESC', 'Instantly switch between 6 standard sizes (Full, TKL, 75%, 65%, 60%, Ergo) and automatically detect your regional profile (ANSI, ISO, JIS).')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('FEAT_WPM_TITLE', '⌨️ Real-Time WPM Test')}</h3>
          <p>${t('FEAT_WPM_DESC', 'Measure your true typing speed and accuracy live using rotating professional pangrams without ever leaving the diagnostic dashboard.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('FEAT_EXPORTS_TITLE', '📤 Comprehensive Exports')}</h3>
          <p>${t('FEAT_EXPORTS_DESC', 'Keep detailed records of your hardware tests. Export raw event logs to CSV, state data to JSON, save a high-res Image Snapshot, or generate a PDF report.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('FEAT_PRIVACY_TITLE', '🛡️ 100% Local Privacy')}</h3>
          <p>${t('FEAT_PRIVACY_DESC', 'We believe your keystrokes belong to you. Our entire toolkit operates strictly client-side within your browser. Zero data transmission. Absolute security.')}</p>
        </article>
      </div>
    </section>

    <!-- ==================== DIAGNOSTICS GUIDE ==================== -->
    <section class="cm-info-section" id="keyboard-guide">
      <div class="cm-info-header">
        <h2 class="cm-info-title">${t('HDR_DIAGNOSTICS')}</h2>
        <p class="cm-info-subtitle">${t('HDR_DIAGNOSTICS_SUB')}</p>
      </div>
      <div class="cm-info-grid">
        <article class="cm-info-card">
          <h3>${t('DIAG_NKRO_TITLE', '🎮 N-Key Rollover (NKRO) & Ghosting')}</h3>
          <p>${t('DIAG_NKRO_DESC', '<strong>NKRO</strong> is the maximum number of keys your keyboard can register simultaneously. Standard USB keyboards are limited to <strong>6KRO</strong> (6 keys + modifiers). Gaming keyboards often support full NKRO via USB or PS/2. <strong>Ghosting</strong> occurs on cheaper membrane keyboards when pressing multiple keys triggers an unintended "ghost" key.')}</p>
          <em>${t('DIAG_NKRO_TEST', 'Test it: Use our NKRO Wizard to check common gaming clusters (WASD + Shift + Space).')}</em>
        </article>
        <article class="cm-info-card">
          <h3>${t('DIAG_CHATTER_TITLE', '⚡ Key Chatter (Bounce Detection)')}</h3>
          <p>${t('DIAG_CHATTER_DESC', 'Mechanical switches can degrade or collect dust, causing a single press to register as <strong>multiple inputs</strong>. This is called chatter or bouncing — it\'s one of the most common mechanical keyboard problems.')}</p>
          <em>${t('DIAG_CHATTER_TEST', 'Test it: Adjust the Chatter Gate threshold (Default: 20ms). Any key registering twice within that window is flagged instantly.')}</em>
        </article>
        <article class="cm-info-card">
          <h3>${t('DIAG_POLLING_TITLE', '📊 Polling Rate & Input Latency')}</h3>
          <p>${t('DIAG_POLLING_DESC', '<strong>Polling rate</strong> (Hz) measures how often your keyboard reports data to the PC. <strong>Latency</strong> (ms) is the delay before the PC registers input. Most keyboards poll at 125Hz (8ms) or 1000Hz (1ms).')}</p>
          <em>${t('DIAG_POLLING_TEST', 'Note: Web browsers limit timing precision (capped around 125–250Hz accuracy). Compare keyboards on the same system for relative results.')}</em>
        </article>
        <article class="cm-info-card">
          <h3>${t('DIAG_FOCUS_TITLE', '🔒 Focus Mode')}</h3>
          <p>${t('DIAG_FOCUS_DESC', '<strong>Focus Mode</strong> dims surrounding UI so you can concentrate on keyboard feedback. Press <code>ESC</code> to exit.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('DIAG_WPM_TITLE', '⌨️ Typing Speed (WPM) Test')}</h3>
          <p>${t('DIAG_WPM_DESC', 'A real-time <strong>words-per-minute (WPM)</strong> test using standard pangrams. Test how your new switches or keyboard feels at speed. Accuracy and WPM are calculated live, giving you instant feedback on your typing performance.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('DIAG_PRIV_TITLE', '🔐 100% Privacy — Zero Data Collection')}</h3>
          <p>${t('DIAG_PRIV_DESC', 'All testing happens <strong>entirely in your browser</strong>. We do <strong>not</strong> transmit, store, or log your keystrokes to any server. You can safely generate PDF reports or export session data knowing your keyboard input stays completely private.')}</p>
        </article>
      </div>
    </section>
      </div> <!-- /cm-below-fold-main -->
      <aside class="cm-below-fold-sidebar cm-sidebar-right" id="sidebar-r1-mount"></aside>
    </div> <!-- /cm-below-fold-layout ROW 1 -->

    <!-- ROW 2: Pro Tips -->
    <div class="cm-below-fold-layout">
      <aside class="cm-below-fold-sidebar cm-sidebar-left" id="sidebar-l2-mount"></aside>
      <div class="cm-below-fold-main">
    <!-- ==================== PRO TIPS ==================== -->
    <section class="cm-info-section" id="pro-tips">
      <div class="cm-info-header">
        <h2 class="cm-info-title">${t('HDR_PRO_TIPS')}</h2>
        <p class="cm-info-subtitle">${t('HDR_PRO_TIPS_SUB')}</p>
      </div>
      <div class="cm-info-grid">
        <article class="cm-info-card">
          <h3>${t('TIP_STORE_TITLE', '💡 Test Before Buying')}</h3>
          <p>${t('TIP_STORE_DESC', 'Shopping for a mechanical keyboard? Bring a laptop to the store and open this tool to instantly test NKRO, chatter, and polling rate on the display unit. No app install needed — it\'s fully web-based.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('TIP_DOUBLE_TITLE', '💡 Diagnose Double-Typing')}</h3>
          <p>${t('TIP_DOUBLE_DESC', 'If a key types twice with a single press, enable <strong>Chatter Detection</strong> and set the gate to 30ms. The affected key will light up in red, confirming it\'s a hardware switch issue — common in keyboards over 2 years old.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('TIP_GAMING_TITLE', '💡 Check Gaming Performance')}</h3>
          <p>${t('TIP_GAMING_DESC', 'For competitive gaming, test the <strong>WASD + Shift + Space + Ctrl</strong> cluster simultaneously. If any key fails to register, your keyboard lacks sufficient rollover for gaming. You need at least <strong>6KRO</strong> for FPS games.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('TIP_COMPARE_TITLE', '💡 Compare Before & After')}</h3>
          <p>${t('TIP_COMPARE_DESC', 'Modded your keyboard with new switches or lube? Export a <strong>JSON baseline</strong> before modding, then test again after. Compare polling rate consistency and chatter levels to verify your mod improved performance.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('TIP_HEATMAP_TITLE', '💡 Use Heatmap for Ergonomics')}</h3>
          <p>${t('TIP_HEATMAP_DESC', 'Enable the <strong>Heatmap</strong> overlay and type normally for 5 minutes. The visualization shows which keys you press most — useful for optimizing custom keyboard layouts or identifying ergonomic hotspots.')}</p>
        </article>
        <article class="cm-info-card">
          <h3>${t('TIP_LAYOUT_TITLE', '💡 Test All Layouts')}</h3>
          <p>${t('TIP_LAYOUT_DESC', 'Switch between <strong>Full, TKL, 75%, 65%, and 60%</strong> layouts to see exactly which keys your keyboard has. Use the <strong>Layout Wizard</strong> to auto-detect whether you have ANSI, ISO, or JIS layout.')}</p>
        </article>
        <article class="cm-info-card cm-info-card--support">
          <h3>${t('SUPPORT_US_TITLE', '💎 Why Advertisement?')}</h3>
          <p>${t('SUPPORT_US_DESC', 'Running a high-performance, real-time diagnostic tool requires significant server and development resources. Ads help us cover these costs and keep the project free for everyone. We appreciate your understanding.')}</p>
        </article>
      </div>
    </section>
      </div> <!-- /cm-below-fold-main -->
      <aside class="cm-below-fold-sidebar cm-sidebar-right" id="sidebar-r2-mount"></aside>
    </div> <!-- /cm-below-fold-layout ROW 2 -->

    <!-- ROW 3: FAQ -->
    <div class="cm-below-fold-layout">
      <aside class="cm-below-fold-sidebar cm-sidebar-left"></aside>
      <div class="cm-below-fold-main">
    <!-- ==================== FAQ ==================== -->
    <section class="cm-info-section" id="faq">
      <div class="cm-info-header">
        <h2 class="cm-info-title">${t('HDR_FAQ')}</h2>
        <p class="cm-info-subtitle">${t('HDR_FAQ_SUB')}</p>
      </div>
      <div class="cm-faq-list">
        <details class="cm-faq-item">
          <summary>${t('FAQ_1_Q', 'What is NKRO (N-Key Rollover) and why does it matter?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_1_A', 'NKRO stands for N-Key Rollover — the number of keys that can be pressed simultaneously and all register correctly. Most standard USB keyboards support 6KRO (6 regular keys plus modifiers like Shift, Ctrl, Alt). Gaming keyboards and high-end mechanical keyboards support full NKRO, meaning every key registers no matter how many are pressed at once. This is critical for gaming (e.g., WASD + Shift + Space + Ctrl combos) and fast typing.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_2_Q', 'What is key chatter (bouncing) and how do I fix it?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_2_A', 'Key chatter (also called key bounce) happens when a single keypress registers as multiple inputs. It\'s caused by worn, dusty, or oxidized switch contacts, especially in mechanical keyboards over 1-2 years old. Our chatter detection tool identifies keys with bounce times under 20ms. Fixes include: (1) cleaning with compressed air or isopropyl alcohol, (2) applying contact cleaner spray, (3) adjusting debounce settings in your firmware, or (4) replacing the switch if hot-swappable.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_3_Q', 'How accurate is the polling rate measurement?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_3_A', 'Browser-based polling rate measurements are estimates, not lab-grade metrics. Browsers cap timing resolution (typically 4ms minimum due to Spectre mitigations), so results above 250Hz may be less precise. Use these numbers to compare keyboards on the same system rather than as absolute values. For exact measurements, use a hardware polling rate tester.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_4_Q', 'Does this work with Bluetooth / wireless keyboards?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_4_A', 'Yes! This tool works with any keyboard your browser can detect — wired USB, wireless 2.4GHz dongle, and Bluetooth. However, wireless keyboards may show higher latency and lower polling rates due to the wireless protocol overhead. For the most accurate diagnostics, use a wired USB connection.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_5_Q', 'Is my keyboard data private?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_5_A', 'Absolutely. Cytific KB Tester runs 100% in your browser. No keystroke data, timing data, or any other information is ever sent to our servers or any third party. Your keyboard input stays completely private. You can verify this by checking the Network tab in your browser\'s developer tools.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_6_Q', 'What is ghosting and how do I test for it?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_6_A', 'Ghosting occurs when pressing certain key combinations causes additional keys to falsely register. This is a hardware limitation of the keyboard\'s matrix design, most common in budget membrane keyboards. Test for it by pressing 3+ keys simultaneously — if extra keys light up that you didn\'t press, your keyboard has ghosting issues.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_7_Q', 'Can I use this on mobile / tablet?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_7_A', 'This tool is designed for physical keyboards connected to desktop or laptop computers. While it may partially work with external Bluetooth keyboards connected to tablets, mobile on-screen keyboards generate different events and won\'t produce meaningful diagnostic results.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_8_Q', 'What browsers are supported?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_8_A', 'Cytific KB Tester works on all modern browsers: Chrome 90+, Firefox 88+, Edge 90+, Safari 14+, Opera, Brave, and Vivaldi. For best results, use the latest version of Chrome or Edge as they provide the most precise keyboard event timing.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_9_Q', 'How many keys can I press at once?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_9_A', 'Standard non-gaming keyboards usually max out at 2 to 6 simultaneous keys (2KRO to 6KRO). If you press more, "ghosting" or dropped inputs occur. Mechanical gaming keyboards often support full N-Key Rollover (NKRO), meaning you can press every key at once.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_10_Q', 'What polling rate is best for gaming?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_10_A', 'For competitive gaming, a polling rate of 1000Hz (1ms delay) is the industry standard. Higher rates like 4000Hz or 8000Hz exist but offer diminishing returns and consume more CPU power. 125Hz (8ms) is typical for standard office keyboards.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_11_Q', 'What is the difference between membrane and mechanical keyboards?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_11_A', 'Membrane keyboards use a rubber dome over a circuit board, offering a softer feel but shorter lifespan. Mechanical keyboards use individual physical switches for each key, providing faster actuation, tactile feedback, superior durability, and full NKRO.')}</p>
          </div>
        </details>
        <details class="cm-faq-item">
          <summary>${t('FAQ_12_Q', 'How do I improve my typing speed?')}</summary>
          <div class="cm-faq-answer">
            <p>${t('FAQ_12_A', 'To improve your WPM, focus on touch typing without looking at the keys. Maintain proper posture, keep your fingers on the home row, and practice regularly. Accuracy is more important than speed — fast typing with mistakes lowers your true WPM.')}</p>
          </div>
        </details>
      </div>
    </section>

    <!-- ==================== KEYBOARD GLOSSARY ==================== -->
    <section class="cm-info-section" id="keyboard-glossary">
      <div class="cm-info-header">
        <h2 class="cm-info-title">${t('GLOSSARY_TITLE', 'KEYBOARD GLOSSARY')}</h2>
        <p class="cm-info-subtitle">${t('GLOSSARY_SUB', 'A beginner\'s guide to essential keyboard terminology and technology')}</p>
      </div>
      <div class="cm-diagnostics-grid">
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_POLLING', 'Polling Rate')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_POLLING', 'The frequency at which the keyboard reports its status to the computer, measured in Hertz (Hz). A 1000Hz polling rate means the keyboard is checked 1,000 times per second, resulting in a maximum potential delay of 1ms.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_LATENCY', 'Input Latency')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_LATENCY', 'The total time delay between physically pressing a key and the corresponding action appearing on screen. Lower latency is crucial for fast-paced gaming where milliseconds matter.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_NKRO', 'N-Key Rollover (NKRO)')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_NKRO', 'The ability of a keyboard to correctly register every single keypress simultaneously, no matter how many keys are held down. Essential for preventing dropped inputs during complex gaming maneuvers.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_GHOSTING', 'Ghosting / Anti-Ghosting')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_GHOSTING', 'Ghosting is a limitation where an unpressed key registers when pressing specific combinations on cheap keyboards. Anti-ghosting is the matrix design used to prevent this from happening in critical gaming clusters (like WASD).')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_APM', 'APM (Actions Per Minute)')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_APM', 'A measurement of a player\'s speed and efficiency in strategy games. It tracks total keystrokes and mouse clicks performed in 60 seconds. High APM indicates fast reflexes and deep game knowledge.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_CPS', 'CPS (Clicks Per Second)')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_CPS', 'A test of pure mechanical speed, measuring how many times a key or button can be pressed in a single second. Very high CPS is usually achieved through techniques like jitter clicking or drag clicking.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_CHATTER', 'Key Chatter (Bounce)')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_CHATTER', 'A hardware malfunction where a mechanical switch triggers multiple times from a single press. It is usually caused by worn physical contacts inside the switch and requires software debounce or switch replacement to fix.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_DEBOUNCE', 'Debounce Time')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_DEBOUNCE', 'The intentional software delay added to ignore the microscopic electrical bouncing that occurs when a switch closes. Setting this too low causes chatter, while setting it too high adds unnecessary latency.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_ACTUATION', 'Actuation Point')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_ACTUATION', 'The physical depth a key must travel down before the electrical contact is made and the keystroke registers. Speed switches have a shorter actuation distance (e.g., 1.2mm) compared to standard switches (2.0mm).')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_MECHANICAL', 'Mechanical Switch')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_MECHANICAL', 'A premium keyboard switch utilizing a physical spring and metal contacts for each individual key. They are renowned for their tactile feel, varied sound profiles, high durability, and gaming performance.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_MEMBRANE', 'Membrane (Rubber Dome)')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_MEMBRANE', 'The most common switch type, using a single layer of rubber domes over a circuit board. They are cheap to manufacture, quiet, and spill-resistant, but feel "mushy" and have limited key rollover.')}</p>
        </div>
        <div class="cm-diag-card">
          <h3 class="cm-diag-card-title">${t('TERM_HOTSWAP', 'Hot-Swappable')}</h3>
          <p class="cm-diag-card-desc">${t('DEF_HOTSWAP', 'A keyboard circuit board design that allows users to easily pull out and replace individual mechanical switches without needing to desolder or solder any connections. Ideal for customization and easy repairs.')}</p>
        </div>
      </div>
    </section>

      </div> <!-- /cm-below-fold-main -->
      <aside class="cm-below-fold-sidebar cm-sidebar-right"></aside>
    </div> <!-- /cm-below-fold-layout ROW 3 -->

    <!-- ==================== TRENDING TAGS (INTERNATIONAL SEO) ==================== -->
    <section class="cm-info-section sr-only" id="trending-tags">
      <div class="cm-info-header">
        <h2 class="cm-info-title">${t('TAGS_TITLE', 'Global Keyboard Tests & Layout Diagnostics')}</h2>
        <p class="cm-info-subtitle">${t('TAGS_SUB', 'International keyboard testing and analysis keywords for search indexing')}</p>
      </div>
      <div class="cm-tags-cloud">
        <!-- English -->
        <span class="cm-tag-pill">Keyboard Tester Online</span>
        <span class="cm-tag-pill">Mac Keyboard Test</span>
        <span class="cm-tag-pill">NKRO Rollover Check</span>
        <span class="cm-tag-pill">Key Chatter Fix</span>
        <span class="cm-tag-pill">Mechanical Keyboard Diagnostics</span>
        <span class="cm-tag-pill">Laptop Keyboard Repair</span>
        
        <!-- Spanish -->
        <span class="cm-tag-pill">Prueba de Teclado</span>
        <span class="cm-tag-pill">Test de Teclado Online</span>
        <span class="cm-tag-pill">Comprobador de Teclado Mecánico</span>
        
        <!-- French -->
        <span class="cm-tag-pill">Testeur de Clavier</span>
        <span class="cm-tag-pill">Test Clavier Mécanique</span>
        <span class="cm-tag-pill">Vérifier Clavier Mac</span>
        
        <!-- German -->
        <span class="cm-tag-pill">Tastatur Test</span>
        <span class="cm-tag-pill">Keyboard Check Online</span>
        <span class="cm-tag-pill">Mechanische Tastatur Prüfen</span>
        
        <!-- Portuguese -->
        <span class="cm-tag-pill">Teste de Teclado Online</span>
        <span class="cm-tag-pill">Testar Teclado Mecânico</span>
        
        <!-- Italian -->
        <span class="cm-tag-pill">Test Tastiera Online</span>
        <span class="cm-tag-pill">Verifica Tastiera Mac</span>
        
        <!-- Russian -->
        <span class="cm-tag-pill">Тест Клавиатуры Онлайн</span>
        <span class="cm-tag-pill">Проверка Клавиатуры Ноутбука</span>
        
        <!-- Japanese -->
        <span class="cm-tag-pill">キーボード テスト</span>
        <span class="cm-tag-pill">キーボードチェッカー</span>
        <span class="cm-tag-pill">Mac キーボード 確認</span>
        
        <!-- Korean -->
        <span class="cm-tag-pill">키보드 테스트</span>
        <span class="cm-tag-pill">기계식 키보드 점검</span>
        
        <!-- Turkish -->
        <span class="cm-tag-pill">Klavye Testi</span>
        <span class="cm-tag-pill">Mekanik Klavye Deneme</span>
        
        <!-- Arabic -->
        <span class="cm-tag-pill">اختبار لوحة المفاتيح</span>
        <span class="cm-tag-pill">اختبار الكيبورد</span>
        <span class="cm-tag-pill">فحص ازرار لوحة المفاتيح</span>
        
        <!-- Chinese (Simplified) -->
        <span class="cm-tag-pill">键盘测试</span>
        <span class="cm-tag-pill">键盘测试仪</span>
        
        <!-- Hindi -->
        <span class="cm-tag-pill">कीबोर्ड टेस्ट</span>
        <span class="cm-tag-pill">कीबोर्ड टेस्टर</span>
        
        <!-- Polish -->
        <span class="cm-tag-pill">Test klawiatury</span>
        <span class="cm-tag-pill">Tester klawiatury</span>
        
        <!-- Swedish -->
        <span class="cm-tag-pill">Tangentbordstest</span>
        <span class="cm-tag-pill">Svenska tangentbordstestare</span>
        
        <!-- Thai -->
        <span class="cm-tag-pill">ทดสอบคีย์บอร์ด</span>
        <span class="cm-tag-pill">เครื่องมือทดสอบคีย์บอร์ด</span>
        
        <!-- Vietnamese -->
        <span class="cm-tag-pill">Kiểm tra bàn phím</span>
        <span class="cm-tag-pill">Công cụ kiểm tra bàn phím</span>
        
        <!-- Hebrew -->
        <span class="cm-tag-pill">בדיקת מקלדת</span>
        <span class="cm-tag-pill">בודק מקלדת</span>
        
        <!-- Greek -->
        <span class="cm-tag-pill">Δοκιμή πληκτρολογίου</span>
        <span class="cm-tag-pill">Ελεγκτής πληκτρολογίου</span>
        
        <!-- Indonesian -->
        <span class="cm-tag-pill">Tes Keyboard</span>
        <span class="cm-tag-pill">Cek Keyboard Online</span>
        
        <!-- Malay -->
        <span class="cm-tag-pill">Ujian Papan Kekunci</span>
        
        <!-- Filipino / Tagalog -->
        <span class="cm-tag-pill">Pagsubok sa Keyboard</span>
        
        <!-- Bengali -->
        <span class="cm-tag-pill">কীবোর্ড টেস্ট</span>
        
        <!-- Tamil -->
        <span class="cm-tag-pill">கீபோர்டு டெஸ்ட்</span>
        
        <!-- Persian / Farsi -->
        <span class="cm-tag-pill">تست کیبورد</span>
        
        <!-- Urdu -->
        <span class="cm-tag-pill">کی بورڈ ٹیسٹ</span>
        
        <!-- Ukrainian -->
        <span class="cm-tag-pill">Тест клавіатури</span>
        
        <!-- Romanian -->
        <span class="cm-tag-pill">Test Tastatură</span>
        
        <!-- Hungarian -->
        <span class="cm-tag-pill">Billentyűzet Teszt</span>
        
        <!-- Czech -->
        <span class="cm-tag-pill">Test Klávesnice</span>
        
        <!-- Danish -->
        <span class="cm-tag-pill">Tastatur Test</span>
        
        <!-- Finnish -->
        <span class="cm-tag-pill">Näppäimistö Testi</span>
        
        <!-- Dutch -->
        <span class="cm-tag-pill">Toetsenbord Test Online</span>
      </div>
    </section>

    <!-- ==================== FOOTER ==================== -->
    <div id="footer-anchor"></div>
    `;

  // Insert horizontal banner ad inside the main content between sections
  const mainContent = container.querySelector('.cm-below-fold-main');
  const sections = mainContent.querySelectorAll('.cm-info-section');

  // Populate Row 1 (Top) Sidebars
  const sidebarL1 = container.querySelector('#sidebar-l1-mount');
  const sidebarR1 = container.querySelector('#sidebar-r1-mount');
  if (sidebarL1 && sidebarR1) {
    const vAd1 = createAdSlot({ id: 'sidebar-l1', width: '300px', height: '600px', label: t('AD_LABEL', 'SPONSORED') });
    const vAd2 = createAdSlot({ id: 'sidebar-r1', width: '300px', height: '600px', label: t('AD_LABEL', 'SPONSORED') });
    sidebarL1.appendChild(vAd1);
    sidebarR1.appendChild(vAd2);
  }

  // Row 2 sidebars — disabled for lean launch. Uncomment to re-enable later.
  // const sidebarL2 = container.querySelector('#sidebar-l2-mount');
  // const sidebarR2 = container.querySelector('#sidebar-r2-mount');
  // if (sidebarL2 && sidebarR2) {
  //   const pAd1 = createAdSlot({ id: 'pro-tips-l', width: '300px', height: '600px', label: t('AD_LABEL', 'SPONSORED') });
  //   const pAd2 = createAdSlot({ id: 'pro-tips-r', width: '300px', height: '600px', label: t('AD_LABEL', 'SPONSORED') });
  //   sidebarL2.appendChild(pAd1);
  //   sidebarR2.appendChild(pAd2);
  // }

  // Footer Ad before the actual footer
  const footerEl = container.querySelector('#footer-anchor');
  if (footerEl) {
    const footerAd = createAdSlot({ id: 'footer-banner', width: '970px', height: '90px', label: t('AD_LABEL', 'SPONSORED') });
    footerAd.classList.add('cm-below-fold-ad');
    container.insertBefore(footerAd, footerEl);
  }


  return container;
};

